import { useEffect, useRef } from 'react'

import { useMemoizedFn, usePagination } from 'ahooks'

import { GenericalRecord, KeySet } from '../../../../types/utilities'
import { toPageApi } from '../../../../utils/crud'
import { getKey } from '../../utils/getKey'
import { useRemoteTransferStoreApi } from '../hooks/useStore'
import { RemoteTransferAction, RemoteTransferState } from '../store/interface'

import { RemoteTransferStoreUpdaterProps } from './interface'

const useStateUpdater = <
  T extends GenericalRecord,
  P extends GenericalRecord,
  K extends KeySet<RemoteTransferState<T, P>>
>(
  key: K,
  value: RemoteTransferState<T, P>[K]
) => {
  const { setState } = useRemoteTransferStoreApi()

  useEffect(() => {
    setState({ [key]: value })
  }, [key, value, setState])
}

const useActionUpdater = <
  T extends GenericalRecord,
  K extends KeySet<RemoteTransferAction<T>>
>(
  key: K,
  value: RemoteTransferAction<T>[K]
) => {
  const { setState } = useRemoteTransferStoreApi()

  useEffect(() => {
    setState({ [key]: value })
  }, [key, value, setState])
}

const DEFAULT_LIST: any[] = []

export const RemoteTransferStoreUpdater = <
  T extends GenericalRecord,
  P extends GenericalRecord
>({
  defaultCurrent,
  defaultPageSize,
  api,
  requestParams,
  readyRequest,
  manualRequest = false,
  refreshDeps,
  formatData,
  rowKey,
  value,
  onChange
}: RemoteTransferStoreUpdaterProps<T, P>) => {
  const pageRef = useRef<{
    current: number
    pages: number
  }>({
    current: 1,
    pages: 0
  })
  const dataCache = useRef<Record<string, T>>({})

  const { getState, setState } = useRemoteTransferStoreApi()

  const { data, error, loading, pagination, runAsync } = usePagination(
    toPageApi<T, GenericalRecord>(api, {
      requestParams,
      format: formatData
    }),
    {
      defaultCurrent: defaultCurrent ?? requestParams?.current ?? 1,
      defaultPageSize: defaultPageSize ?? requestParams?.pageSize ?? 20,
      manual: manualRequest,
      ready: readyRequest,
      refreshDeps,
      onSuccess({ current, pages, list }, params) {
        getState().setRequestParams(params[0])

        pageRef.current = {
          current,
          pages
        }
        // 刷新时清空缓存
        if (current === 1) {
          dataCache.current = {}
        }
        if (list) {
          dataCache.current = list.reduce(
            (acc, cur) => {
              acc[getKey(cur, rowKey)] = cur
              return acc
            },
            { ...dataCache.current }
          )
        }
        // 更新 allData 到 store，避免在渲染期间读取 ref
        setState({ allData: dataCache.current })
      }
    }
  )

  const fetchSearch = useMemoizedFn((params: GenericalRecord) => {
    return runAsync({
      ...getState().requestParams,
      ...params,
      current: 1
    } as any)
  })

  const fetchNextPage = useMemoizedFn(() => {
    if (loading || pageRef.current.current >= pageRef.current.pages) {
      return
    }
    return runAsync({
      ...getState().requestParams,
      current: pageRef.current.current + 1
    } as any)
  })

  const reload = useMemoizedFn((reset?: boolean) => {
    return runAsync({
      ...getState().requestParams,
      current: reset ? 1 : pageRef.current.current
    } as any)
  })

  // allData 已在 onSuccess 中写入，避免在渲染期间读取 ref
  // useStateUpdater('allData', dataCache.current)
  useStateUpdater('data', data?.list ?? DEFAULT_LIST)
  useStateUpdater('error', error)
  useStateUpdater('loading', loading)
  useStateUpdater('pagination', pagination)

  useActionUpdater('reload', reload)
  useActionUpdater('fetchSearch', fetchSearch)
  useActionUpdater('fetchNextPage', fetchNextPage)

  useStateUpdater('value', value ?? DEFAULT_LIST)
  useStateUpdater('onChange', onChange)

  return null
}
